/**
 * [PURPOSE]: Automatically register boon passive effects with effectRegistry
 *   whenever playerStats.customBoons changes. Called from App.jsx (always-mounted),
 *   mirroring useFeatEffects.js — ensures boon effects fire on all tabs, not just
 *   when a boon-aware panel is rendered.
 *
 * [SUB-PLAN 3A — 2026-05-30]: Boon Creator arc. New storage slot
 *   playerStats.customBoons[] (array of { id, name, description, passiveEffects[] … }).
 *   This hook is the registration consumer — Rule 25 same-commit consumer contract.
 *
 * [REGISTRATION PATH]:
 *   playerStats.customBoons → effectComposerRowsToEffects(boon.passiveEffects)
 *   → effectRegistry.register('boon:<id>', effects, character)
 *   → effectCalculator.getEffectiveValue reads boon bonuses alongside feat/race effects.
 *   (Unified dispatcher: handles passiveBonus + resistance rows; resistance rows authored
 *   through EffectComposer will be registered correctly — getDamageResistances() reads them.)
 *
 * [FORK 3A-Q3 RESOLVED — C (skip active check)]:
 *   ALL boons are registered unconditionally in v1.0. The `active` boolean is stored
 *   but NOT checked here. Rationale: the active-toggle UI (3A-followup-active-toggle)
 *   doesn't exist yet. Gating on boon.active with a false default would make every
 *   boon silently inert — the exact "inert data" anti-pattern. The gate lands in the
 *   same commit as the toggle UI.
 *   // [BREADCRUMB v1.x 3A-followup-active-toggle]: add `if (!boon.active) return;`
 *   //   guard here when the Boons/Feats panel merge ships the toggle UI.
 *
 * [RULE 22]: effectComposerRowsToEffects is the SHARED production converter (src/utils/passiveBonusRowToEffect.js).
 *   No inline replica — both this hook and the bridge test import the same function.
 *
 * @param {Object} character - playerStats (full object; reads character.customBoons)
 * @param {Function} [logActivity] - optional activity logger (same signature as App.jsx's logActivity)
 */

import { useEffect } from 'react';
import { effectRegistry } from '../services/effectRegistry';
import { systemBus, EVENTS } from '../services/systemBus';
// [3A LATENT MISMATCH FIX — 2026-05-30]: Replace passiveBonusRowsToEffects (passiveBonus-only)
//   with effectComposerRowsToEffects (unified dispatcher). EffectComposer exposes the
//   'resistance' kind selector to boon authors — if a user adds a resistance row to a boon and
//   this hook still calls the old passiveBonus-only converter, the resistance row is silently
//   dropped (passiveBonusRowToEffect returns null for rows without an `attribute` field).
//   effectComposerRowsToEffects dispatches on `row.kind` and routes resistance rows through
//   resistanceRowToEffect correctly. No behavior change for existing passiveBonus rows.
import { effectComposerRowsToEffects } from '../utils/passiveBonusRowToEffect';

export const useBoonEffects = (character, logActivity = null) => {
  useEffect(() => {
    // [CLEANUP]: Clear all previously-registered boon effects to avoid stale duplicates.
    // removeByPrefix('boon:') removes every source whose id starts with 'boon:'.
    effectRegistry.removeByPrefix('boon:');

    const boons = character?.customBoons || [];

    boons.forEach(boon => {
      if (!boon || !boon.id) return;

      const rows = boon.passiveEffects || [];
      // [PRODUCTION BRIDGE]: effectComposerRowsToEffects converts any EffectComposer row
      // (passiveBonus or resistance) to effectRegistry-native { type, operation, value, condition }.
      // Rule 31f: test must call this same function starting from the storage shape.
      const effects = effectComposerRowsToEffects(rows);

      if (effects.length > 0) {
        try {
          effectRegistry.register(`boon:${boon.id}`, effects, character);
        } catch (err) {
          console.warn(`[useBoonEffects] Failed to register effects for boon "${boon.name}" (${boon.id}):`, err);
        }
      }
    });

    // [ARC-012 PATTERN]: Emit EFFECTS_CHANGED so other systems invalidate their caches.
    systemBus.emit(EVENTS.EFFECTS_CHANGED, {
      action: 'boon_effects_registered',
      boonCount: boons.length,
      source: 'useBoonEffects',
    });

    // [RULE 13 — activity log]: Log re-registration only when boons are present.
    if (logActivity && boons.length > 0) {
      const registeredCount = boons.filter(b => b && (b.passiveEffects || []).length > 0).length;
      if (registeredCount > 0) {
        logActivity(
          `Registered ${registeredCount} boon effect${registeredCount !== 1 ? 's' : ''} with EffectRegistry`,
          'system',
          { totalBoons: boons.length, registeredEffects: registeredCount, boonIds: boons.map(b => b.id) }
        );
      }
    }
  }, [character?.customBoons, logActivity]);
};

export default useBoonEffects;
